import { RetailerCheckoutOrderStatusV2 } from "@/api/rest/checkoutApi";
import { ProductCardProps } from "@/components/Checkout/components/LegacyCheckoutFlow/LegacyEnterCheckoutAnimation";
import { AppDispatch } from "@/redux/store";
import { MfaValues } from "@/types/misc";
import { NavigateFunction } from "react-router-dom";
import { RetailerConnectionState } from "../../../../types/misc";
import {
  UserInteractionRequirement,
  UserInteractionType,
  UserRetailerCheckoutBatch,
  UserRetailerCheckoutOrder,
  UserRetailerShoppingCart,
} from "../../types";
import { DoneActorEvent, ErrorActorEvent } from "xstate";
import { ProductRewardSearchResult } from "@brandclub/common-ui";

type RetailerConnectionInfoList = {
  retailerId: number;
  credential: {
    username?: string;
    password?: string;
    connectionStatus: RetailerConnectionState;
  };
}[];

export enum FastCheckoutState {
  LOADING = "LOADING",
  PROCESSING = "PROCESSING",
  ACTION_REQUIRED_ERROR = "ACTION_REQUIRED_ERROR",
  NON_ACTION_REQUIRED_ERROR = "NON_ACTION_REQUIRED_ERROR",
  RECEIPT_GENERATED = "RECEIPT_GENERATED",
}

export enum ACTION_REQUIRED_ERROR {
  LOGIN_ERROR = "LOGIN_ERROR",
  OTP_ERROR = "OTP_ERROR", // do polling, only exception for polling
  OTP_TIMEOUT_ERROR = "OTP_TIMEOUT_ERROR",
  SHIPPING_ERROR = "SHIPPING_ERROR",
  DELIVERY_PARTIALLY_OUT_OF_STOCK_ERROR = "DELIVERY_PARTIALLY_OUT_OF_STOCK_ERROR",
}

export enum NON_ACTION_REQUIRED_ERROR {
  PAYMENT_ERROR = "PAYMENT_ERROR",
  DELIVERY_ALL_OUT_OF_STOCK_ERROR = "DELIVERY_ALL_OUT_OF_STOCK_ERROR",
}

export enum FastUserInputUpdateType {
  UPDATE_PAYMENT_CONFIRMATION_AND_COMPLETE_CHECKOUT = "UPDATE_PAYMENT_CONFIRMATION_AND_COMPLETE_CHECKOUT",
  UPDATE_PAYMENT_METHOD = "UPDATE_PAYMENT_METHOD",
  UPDATE_SHIPPING_ADDRESS = "UPDATE_SHIPPING_ADDRESS",
  UPDATE_SHIPPING_OPTION = "UPDATE_SHIPPING_OPTION",
  ADD_SHIPPING_ADDRESS = "ADD_SHIPPING_ADDRESS",
  ADD_PAYMENT_METHOD = "ADD_PAYMENT_METHOD",
  REMOVE_PARTIALLY_OUT_OF_STOCK_ITEM = "REMOVE_PARTIALLY_OUT_OF_STOCK_ITEM",
}

export type SessionOpenState = {
  open?: boolean;
  chevronShow?: boolean;
  errorMessage?: string;
};

export type FastCheckoutMachineContext = {
  orderId?: string;
  sectionState: {
    account: SessionOpenState;
    payment: SessionOpenState;
    shipping: SessionOpenState;
    delivery: SessionOpenState;
  };
  products: Record<string, ProductRewardSearchResult>;
  checkoutState?: FastCheckoutState;
  retailerIds?: number[];
  retailerConnectionInfoList?: RetailerConnectionInfoList;
  activeCheckoutRetailerId?: number;
  checkoutBatch?: UserRetailerCheckoutBatch;
  activeCheckoutOrder?: UserRetailerCheckoutOrder;
  retailerCheckoutOrderStatus?: RetailerCheckoutOrderStatusV2;
  checkoutAllCarts?: Partial<UserRetailerShoppingCart>[];
  error?: {
    stage?: number;
    type: ACTION_REQUIRED_ERROR | NON_ACTION_REQUIRED_ERROR;
    interactionRequired?: UserInteractionRequirement;
    errorMessage?: string;
  };
  activeStage?: number;
  userInputError?: Error;
  enterAnimationConfig?: {
    title: string;
    checkoutCards: ProductCardProps[];
  };
  otpStatus?: MfaValues;
  checkoutRetailerConnectionState?: {
    retailerId: number;
    type: "INITIAL_CONNECT" | "RETRY_LOGIN";
    errorMessage?: string;
  };
  navigate: NavigateFunction;
  dispatch: AppDispatch;
  userInputUpdateType?: FastUserInputUpdateType;
  animationCompleted?: boolean;
  /**
   * Indicates if the user had to enter login info for a retailer.
   */
  hasUserLoggedInRetailer?: boolean;
};

export type FastCheckoutSectionOpenEvent = {
  type: "ON_SESSION_OPEN";
  data: {
    sessionToOpen: "account" | "payment" | "shipping" | "delivery";
  };
};

export type FastCheckoutSectionCloseEvent = {
  type: "ON_SESSION_CLOSE";
  data: {
    sessionToClose: "account" | "payment" | "shipping" | "delivery";
  };
};

export type FastCheckoutRetryCheckoutEvent = {
  type: "ON_RETRY_CHECKOUT";
};

export type FastCheckoutSubmitLoginInfoEvent = {
  type: "SUBMIT_LOGIN_INFO";
  data: {
    username?: string;
    password?: string;
  };
};

export type FastCheckoutSubmitOtpEvent = {
  type: "SUBMIT_OTP";
  data: {
    otp?: string;
  };
};

export type FastCheckoutResendOtpEvent = {
  type: "RESEND_OTP";
};

export type FastCheckoutOtpTimeoutRetryEvent = {
  type: "OTP_TIME_OUT_RETRY";
};

export type FastCheckoutUpdateShippingAddressEvent = {
  type: "UPDATE_SHIPPING_ADDRESS";
};

export type FastCheckoutConfirmPartiallyOutOfStockEvent = {
  type: "CONFIRM_PARTIALLY_OUT_OF_STOCK";
};

export type FastCheckoutSubmitUserInteractionEvent = {
  type: "SUBMIT_USER_INTERACTION";
  data: {
    interactionType: UserInteractionType;
    interactionData: UserRetailerCheckoutCommunicationResponse;
  };
};

export type FastCheckoutMachineEvents =
  | FastCheckoutSectionOpenEvent
  | FastCheckoutSectionCloseEvent
  | FastCheckoutSubmitLoginInfoEvent
  | FastCheckoutSubmitOtpEvent
  | FastCheckoutResendOtpEvent
  | FastCheckoutOtpTimeoutRetryEvent
  | FastCheckoutUpdateShippingAddressEvent
  | FastCheckoutConfirmPartiallyOutOfStockEvent
  | DoneActorEvent
  | ErrorActorEvent
  | FastCheckoutSubmitUserInteractionEvent
  | FastCheckoutRetryCheckoutEvent;

export type UserRetailerCheckoutRetailerCredentialsCommunicationResponse = {
  credential: {
    username: string;
    password: string;
  };
  retailerId: number;
};

export type UserRetailerCheckoutOtpCommunicationResponse = {
  otpCode?: string;
  resend?: boolean;
};

export type UserRetailerCheckoutShippingAddressSelectionCommunicationResponse =
  {
    address1: string;
    address2: string;
    city: string;
    state: string;
    zipCode: string;
    firstName: string;
    lastName: string;
  };

export type UserRetailerCheckoutInventoryConfirmationCommunicationResponse = {
  approve: boolean;
};

export type UserRetailerCheckoutShippingOptionSelectionCommunicationResponse = {
  shippingOption: {
    shippingGroups: {
      products: {
        retailerSku: string;
        stacklineSku?: string;
        quantity: number;
        retailPrice: number;
      }[];
      deliveryOptions: {
        name: string;
        cssSelector: string;
        deliveryTimeWindows?: {
          name: string;
          cssSelector: string;
          isSelected: boolean;
        }[];
        isSelected: boolean;
      }[];
    }[];
  };
};

export type UserRetailerCheckoutCommunicationResponse =
  | UserRetailerCheckoutRetailerCredentialsCommunicationResponse
  | UserRetailerCheckoutOtpCommunicationResponse
  | UserRetailerCheckoutShippingAddressSelectionCommunicationResponse
  | UserRetailerCheckoutInventoryConfirmationCommunicationResponse
  | UserRetailerCheckoutShippingOptionSelectionCommunicationResponse;
